import React, { useMemo, useCallback, useRef, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Box,
  Typography,
  styled,
  Pagination,
  Stack,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

const VirtualizedTableContainer = styled(TableContainer)(({ theme }) => ({
  '& .VirtualTable-tableContainer': {
    position: 'relative',
  },
  '& .VirtualTable-table': {
    tableLayout: 'fixed',
    width: '100%',
  },
  '& .VirtualTable-tableHead': {
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 10,
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  '& .VirtualTable-row': {
    display: 'flex',
    alignItems: 'stretch',
    boxSizing: 'border-box',
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: '64px',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  '& .VirtualTable-cell': {
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(0.75, 0.5),
    boxSizing: 'border-box',
    overflow: 'visible',
    minHeight: '100%',
  },
}));

interface VirtualizedMovementTableProps {
  data: any[];
  columns: {
    field: string;
    headerName: string;
    width?: number;
    flex?: number;
    renderCell: (item: any, index: number) => React.ReactNode;
  }[];
  itemHeight?: number;
  height?: number | string;
  onScroll?: (scrollTop: number) => void;
  onAddNewRow?: () => void;
  emptyMessage?: string;
  ensureEmptyRow?: boolean;
  itemsPerPage?: number;
  showPagination?: boolean;
  dense?: boolean;
  rowHeight?: 'compact' | 'standard' | 'comfortable';
}

interface RowProps {
  index: number;
  style: React.CSSProperties;
  data: {
    items: any[];
    columns: VirtualizedMovementTableProps['columns'];
    onAddNewRow?: () => void;
  };
}

const Row: React.FC<RowProps> = React.memo(({ index, style, data }) => {
  const { items, columns, onAddNewRow } = data;
  const item = items[index];
  
  if (!item) {
    return null;
  }

  const totalColumnWidth = columns.reduce((sum, col) => sum + (col.width || 150), 0);

  return (
    <div style={{ ...style, display: 'flex', width: '100%' }} className="VirtualTable-row">
      {columns.map((column, columnIndex) => {
        const cellWidth = column.width || 150;
        const flexBasis = `${(cellWidth / totalColumnWidth) * 100}%`;
        
        return (
          <div
            key={`${column.field}-${index}-${columnIndex}`}
            className="VirtualTable-cell"
            style={{
              flex: `0 0 ${flexBasis}`,
              minWidth: cellWidth * 0.8,
              borderRight: columnIndex < columns.length - 1 ? '1px solid rgba(224, 224, 224, 0.3)' : 'none',
              overflow: 'visible',
              pointerEvents: 'auto'
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {column.renderCell(item, index)}
          </div>
        );
      })}
    </div>
  );
});

Row.displayName = 'VirtualizedTableRow';

const VirtualizedMovementTable: React.FC<VirtualizedMovementTableProps> = ({
  data,
  columns,
  itemHeight = 72,
  height = 400,
  onScroll,
  onAddNewRow,
  emptyMessage = 'Start scanning barcodes to add items',
  ensureEmptyRow = true,
  itemsPerPage: initialItemsPerPage = 25,
  showPagination = false,
  dense = true,
  rowHeight = 'compact'
}) => {
  const listRef = useRef<List>(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

  const displayData = useMemo(() => {
    if (!ensureEmptyRow) return data;
    
    const hasEmptyRow = data.some(item => !item.barcode || !item.barcode.trim());
    if (hasEmptyRow && data.length > 0) {
      return data;
    }
    
    const newId = `empty-${Date.now()}`;
    const emptyRow = {
      id: newId,
      barcode: '',
      searchResult: null,
      quantity: 1,
      loading: false
    };
    
    return data.length === 0 ? [emptyRow] : [...data, emptyRow];
  }, [data, ensureEmptyRow]);

  const filteredData = useMemo(() => {
    return displayData.filter(item => item.barcode?.trim());
  }, [displayData]);

  const totalItems = filteredData.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

const paginatedData = useMemo(() => {
   if (!showPagination) return displayData;
   const startIndex = (page - 1) * itemsPerPage;
   const endIndex = startIndex + itemsPerPage;
   let pageSlice = displayData.slice(startIndex, endIndex);
   if (ensureEmptyRow && !pageSlice.some(it => !it.barcode?.trim())) {
     pageSlice = [
       ...pageSlice,
       { id: `empty-${Date.now()}`, barcode: '', searchResult: null, quantity: 1, loading: false },
     ];
   }
   return pageSlice;
 }, [displayData, page, itemsPerPage, showPagination, ensureEmptyRow]);


  const itemData = useMemo(() => ({
    items: paginatedData,
    columns,
    onAddNewRow
  }), [paginatedData, columns, onAddNewRow]);

  const handlePageChange = useCallback((_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((event: any) => {
    const newItemsPerPage = event.target.value;
    setItemsPerPage(newItemsPerPage);
    setPage(1);
  }, []);

  const stats = useMemo(() => {
    const ready = filteredData.filter(item => item.barcode?.trim()).length;
    const unknown = filteredData.filter(item => item.error).length;
    const found = filteredData.filter(item => item.itemName || item.boxDetails).length;
    return { ready, unknown, found };
  }, [filteredData]);

  const handleScroll = useCallback(({ scrollTop }: { scrollTop: number }) => {
    onScroll?.(scrollTop);
  }, [onScroll]);

  const totalWidth = useMemo(() => {
    const calculatedWidth = columns.reduce((total, col) => {
      return total + (col.width || 150);
    }, 0);
    return Math.max(calculatedWidth, 1200);
  }, [columns]);

  const actualItemHeight = useMemo(() => {
    if (itemHeight) return itemHeight;
    switch (rowHeight) {
      case 'compact': return 56;
      case 'standard': return 72;
      case 'comfortable': return 88;
      default: return 56;
    }
  }, [itemHeight, rowHeight]);

  const calculatedHeight = useMemo(() => {
    if (typeof height === 'number') {
      return height;
    }
    if (height === 'auto') {
      const headerHeight = 56;
      const footerHeight = 8; 
      const paginationHeight = showPagination ? 80 : 0;
      const topControlsHeight = 0;
      
      const maxVisibleRows = showPagination ? Math.min(itemsPerPage, 8) : 10;
      const visibleRows = Math.min(paginatedData.length || 1, maxVisibleRows);
      const rowsHeight = visibleRows * actualItemHeight;
      
      const minHeight = 320;
      const maxHeight = 480;
      
      const totalHeight = headerHeight + rowsHeight + footerHeight + paginationHeight + topControlsHeight;
      return Math.min(Math.max(totalHeight, minHeight), maxHeight);
    }
    if (typeof height === 'string') {
      const parsed = parseInt(height);
      return isNaN(parsed) ? 400 : parsed;
    }
    return 400; 
  }, [height, paginatedData.length, actualItemHeight, showPagination, itemsPerPage]);
  
  const listHeight = Math.max(calculatedHeight - 64, 100);

  if (paginatedData.length === 0) {
    return (
      <Paper variant="outlined">
        <VirtualizedTableContainer>
          <Box 
            sx={{ 
              height: 200, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: 'text.secondary'
            }}
          >
            <Typography variant="body2">{emptyMessage}</Typography>
          </Box>
        </VirtualizedTableContainer>
      </Paper>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      minHeight: showPagination ? '400px' : '300px',
      maxHeight: height === 'auto' ? '600px' : undefined
    }}>

      {/* Table */}
      <Paper variant="outlined" sx={{ 
        width: '100%', 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0,
        borderRadius: 1,
        overflow: 'hidden'
      }}>
        <VirtualizedTableContainer sx={{ height, width: '100%', flex: '1 1 auto' }}>
          {/* Fixed Header */}
          <div className="VirtualTable-tableHead" style={{ 
            display: 'flex', 
            backgroundColor: '#f5f5f5',
            borderBottom: '2px solid rgba(224, 224, 224, 1)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            width: '100%',
            backdropFilter: 'blur(8px)'
          }}>
            {columns.map((column, columnIndex) => {
              const cellWidth = column.width || 150;
              const totalColumnWidth = columns.reduce((sum, col) => sum + (col.width || 150), 0);
              const flexBasis = `${(cellWidth / totalColumnWidth) * 100}%`;
              
              return (
                <div
                  key={column.field}
                  style={{
                    flex: `0 0 ${flexBasis}`,
                    minWidth: cellWidth * 0.8,
                    padding: rowHeight === 'compact' ? '8px' : '12px 16px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    borderRight: columnIndex < columns.length - 1 ? '1px solid rgba(224, 224, 224, 0.3)' : 'none',
                    backgroundColor: '#f5f5f5',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {column.headerName}
                </div>
              );
            })}
          </div>

          <Box className="VirtualTable-tableContainer" sx={{ 
            height: listHeight,
            overflow: 'hidden'
          }}>
            <List
              ref={listRef}
              height={listHeight}
              itemCount={paginatedData.length}
              itemSize={actualItemHeight}
              itemKey={(index, data) => data.items[index]?.id ?? index}
              itemData={itemData}
              onScroll={handleScroll}
              style={{
                width: '100%',
                minWidth: '100%',
              }}
              overscanCount={2}
            >
              {Row}
            </List>
          </Box>
        </VirtualizedTableContainer>
      </Paper>

      <Box sx={{
        borderTop: '1px solid rgba(224, 224, 224, 0.5)',
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        minHeight: '8px',
        flexShrink: 0
      }} />

      {showPagination && (
        <Box sx={{ 
          mt: 2, 
          p: 2,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderTop: '2px solid rgba(224, 224, 224, 1)',
          backgroundColor: '#f8f9fa',
          borderRadius: '0 0 8px 8px',
          flexShrink: 0,
          position: 'relative',
          zIndex: 5
        }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {totalItems > 0 ? (
                <>Showing {Math.min((page - 1) * itemsPerPage + 1, totalItems)} to {Math.min(page * itemsPerPage, totalItems)} of {totalItems} items</>
              ) : (
                <>0 items - ready to scan barcodes</>
              )}
            </Typography>
            <Chip 
              label={`Page ${page} of ${totalPages}`} 
              size="small" 
              variant="outlined"
              color="primary"
            />
          </Stack>
          
          <Stack direction="row" spacing={2} alignItems="center">
            <Pagination
              count={Math.max(totalPages, 1)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              disabled={totalPages <= 1}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 500,
                  fontSize: '0.875rem'
                },
                '& .MuiPaginationItem-page.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark'
                  }
                }
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Per Page</InputLabel>
              <Select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                label="Per Page"
              >
                <MenuItem value={10}>10 rows</MenuItem>
                <MenuItem value={15}>15 rows</MenuItem>
                <MenuItem value={20}>20 rows</MenuItem>
                <MenuItem value={25}>25 rows</MenuItem>
                <MenuItem value={50}>50 rows</MenuItem>
                <MenuItem value={100}>100 rows</MenuItem>
                {totalItems > 100 && <MenuItem value={totalItems}>All ({totalItems})</MenuItem>}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default VirtualizedMovementTable;